import React from "react";
import { Icon, Panel, Grid, classnames } from "@culturehq/components";

import useGet, { GetState } from "app/hooks/useGet";
import { formatThousand, t, Trans } from "app/utils";

import { Params } from "@culturehq/client";
import styles from "./event-status.module.scss";
import Change from "./Change";

type PostersStats = {
  total: number;
  change: number;
};

const UniqueStoryPosters: React.FC<{
  posters: PostersStats; showChange: boolean;}> = ({ posters, showChange }) => (
    <Panel>
      <Panel.Body className="tile_view">
        <div className={classnames(styles.icon, styles.yellow)}>
          <Icon icon="user" />
        </div>
        <h2>{formatThousand(posters.total)}</h2>
        <div>
          <h4><Trans>Unique Social Posters</Trans></h4>
        </div>
        {showChange && <Change change={posters.change} />}
      </Panel.Body>
    </Panel>
  );

type PostsStats = {
  total: number;
  change: number;
};

const TotalStories: React.FC<{
  posts: PostsStats; showChange: boolean;}> = ({ posts, showChange }) => (
    <Panel>
      <Panel.Body className="tile_view">
        <div className={classnames(styles.icon, styles.blue)}>
          <Icon icon="book" />
        </div>
        <h2>{formatThousand(posts.total)}</h2>
        <div>
          <h4><Trans>Total Social Posts</Trans></h4>
        </div>
        {showChange && <Change change={posts.change} />}
      </Panel.Body>
    </Panel>
  );

type EngagementStats = {
  total: number;
  change: number;
};

const TotalEngagement: React.FC<{
  engagement: EngagementStats; showChange: boolean;}> = ({ engagement, showChange }) => (
    <Panel>
      <Panel.Body className="tile_view">
        <div className={classnames(styles.icon, styles.green)}>
          <Icon icon="people" />
        </div>
        <h2>{formatThousand(engagement.total)}</h2>
        <div className="card_infoicn">
          <h4 className="m-r-5">
            <Trans>Total Engagement</Trans>
          </h4>
        </div>
        {showChange && <Change change={engagement.change} />}
      </Panel.Body>
    </Panel>
  );

const SocialStatusGetting: React.FC = () => (
  <Grid>
    <Grid.Item md={6} lg={4}>
      <Panel>
        <Panel.LoaderBody loading className="tile_view" />
      </Panel>
    </Grid.Item>
    <Grid.Item md={6} lg={4}>
      <Panel>
        <Panel.LoaderBody loading className="tile_view" />
      </Panel>
    </Grid.Item>
    <Grid.Item md={12} lg={4}>
      <Panel>
        <Panel.LoaderBody loading className="tile_view" />
      </Panel>
    </Grid.Item>
  </Grid>
);

type SocialStats = {
  stats: {
    posts: PostsStats;
    engagement: EngagementStats;
    posters: PostersStats;
  };
};

type SocialStatusProps = {
  get: GetState<SocialStats>;
  params: Params;
};

const SocialStatus: React.FC<SocialStatusProps> = ({ params }) => {
  const get: any = useGet("/analytics/social/stats", params);

  if (get.getting) {
    return <SocialStatusGetting />;
  }

  if (get.error) {
    throw new Error(t("Looks like there has been an unexpected error. Please reload the page."));
  }

  return (
    <Grid>
      <Grid.Item md={6} lg={4}>
        <TotalStories posts={get.got.stats.posts} showChange={!!params?.startDate} />
      </Grid.Item>
      <Grid.Item md={6} lg={4}>
        <TotalEngagement engagement={get.got.stats.engagement} showChange={!!params?.startDate} />
      </Grid.Item>
      <Grid.Item md={12} lg={4}>
        <UniqueStoryPosters posters={get.got.stats.posters} showChange={!!params?.startDate} />
      </Grid.Item>
    </Grid>
  );
};

export default SocialStatus;
